import { Column, Id, LaravelResponse } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { useField } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FeedbackEvaluation } from "../../../../shared/feedback/FeedbackEvaluation";
import { eventService } from "../../shared/api/event/eventService";
import { AnalyticsFeedbackEventOptionResource } from "../analyticsTypes";
import { AnalyticsEventSelect, defaultAnalyticsEventFetchConfig } from "./AnalyticsEventSelect";
import { EventInfo } from "./EventInfo";
import { MetricsOverview } from "./MetricsOverview";

const Grid = styled.div<{ $sideBySide: boolean }>`
	display: grid;
	grid-template-columns: ${({ $sideBySide }) => ($sideBySide ? "1fr 1fr" : "1fr")};
	gap: ${({ theme }) => theme.spacing(12)};
	align-items: baseline;
`;

const SingleColumn = styled.div`
	display: grid;
	gap: ${({ theme }) => theme.spacing(12)};
`;

const Empty = styled.div`
	${({ theme }) => theme.content.empty}
	padding-top: ${({ theme }) => theme.spacing(10)};
	padding-bottom: ${({ theme }) => theme.spacing(24)};
`;

interface Props {
	compareEnabled: boolean;
}

export const FeedbackAnalyticsContent: FC<Props> = ({ compareEnabled }) => {
	const { t } = useTranslation();

	const [{ value: selectedEventOption }] = useField<AnalyticsFeedbackEventOptionResource | null>("selectedEvent");
	const [{ value: eventToCompareOption }, , eventToCompareHelpers] =
		useField<AnalyticsFeedbackEventOptionResource | null>("eventToCompare");

	const selectedEventId = selectedEventOption?.id;
	const eventToCompareId = eventToCompareOption?.id;

	const compareFetchConfig = {
		...defaultAnalyticsEventFetchConfig,
		dataMapper: (res: LaravelResponse<AnalyticsFeedbackEventOptionResource[]>) =>
			res.data.filter((event) => event.id !== selectedEventId),
	};

	const { data: selectedEvent } = useQuery({
		queryKey: eventService.showEventForFeedbackAnalyticsKey(selectedEventId as Id),
		queryFn: () => eventService.showEventForFeedbackAnalytics(selectedEventId as Id).then((x) => x.data.data),
		enabled: selectedEventId != null,
	});

	const { data: eventToCompare } = useQuery({
		queryKey: eventService.showEventForFeedbackAnalyticsKey(eventToCompareId as Id),
		queryFn: () => eventService.showEventForFeedbackAnalytics(eventToCompareId as Id).then((x) => x.data.data),
		enabled: eventToCompareId != null,
	});

	return (
		<>
			<Grid $sideBySide={compareEnabled}>
				<AnalyticsEventSelect
					name="selectedEvent"
					label={t("Event")}
					onChange={() => {
						void eventToCompareHelpers.setValue(null);
					}}
				/>
				{compareEnabled && (
					<AnalyticsEventSelect name="eventToCompare" label={t("Event to compare")} fetchConfig={compareFetchConfig} />
				)}
				{selectedEvent && eventToCompare ? (
					<>
						<EventInfo event={selectedEvent} />
						<EventInfo event={eventToCompare} />
						<Column $rowGap={5}>
							{selectedEvent.feedbackEvaluation.map((evaluation) => (
								<FeedbackEvaluation key={evaluation.feedback.id} eventId={selectedEvent.id} feedback={evaluation} />
							))}
						</Column>
						<Column $rowGap={5}>
							{eventToCompare.feedbackEvaluation.map((evaluation) => (
								<FeedbackEvaluation key={evaluation.feedback.id} eventId={eventToCompare.id} feedback={evaluation} />
							))}
						</Column>
						<MetricsOverview metrics={selectedEvent.metrics} />
						<MetricsOverview metrics={eventToCompare.metrics} />
					</>
				) : (
					<SingleColumn>
						{selectedEvent && <EventInfo event={selectedEvent} />}
						{eventToCompare && <EventInfo event={eventToCompare} />}
						{selectedEvent && (
							<Column $rowGap={5}>
								{selectedEvent.feedbackEvaluation.map((evaluation) => (
									<FeedbackEvaluation key={evaluation.feedback.id} eventId={selectedEvent.id} feedback={evaluation} />
								))}
							</Column>
						)}
						{eventToCompare && (
							<Column $rowGap={5}>
								{eventToCompare.feedbackEvaluation.map((evaluation) => (
									<FeedbackEvaluation key={evaluation.feedback.id} eventId={eventToCompare.id} feedback={evaluation} />
								))}
							</Column>
						)}
						{selectedEvent && <MetricsOverview metrics={selectedEvent.metrics} />}
						{eventToCompare && <MetricsOverview metrics={eventToCompare.metrics} />}
					</SingleColumn>
				)}
			</Grid>
			{!selectedEvent && !eventToCompare && <Empty>{t("No event selected.")}</Empty>}
		</>
	);
};
